import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Users,
  Eye,
  Star,
  Tag,
  ArrowLeft,
  DollarSign,
  Clock,
  Share2
} from 'lucide-react';
import api from '../../utils/axios.config';
import { baseUrl } from '../../utils/url';

const ActivityDetail = () => {
  const { id } = useParams();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedActivities, setRelatedActivities] = useState([]);

  // Fetch activity details
  const fetchActivity = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`recent-activities/${id}`);
      
      if (data.success) {
        setActivity(data.data.activity);
        
        // Fetch related activities
        if (data.data.activity.category) {
          fetchRelatedActivities(data.data.activity.category._id, id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    } finally {
      setLoading(false);
    }
  };

  // Increment view count (separate function to avoid multiple calls)
  const incrementViewCount = async () => {
    const sessionKey = `activity_viewed_${id}`;
    
    // Check if we've already incremented view for this activity in this session
    if (sessionStorage.getItem(sessionKey) || !id) {
      return;
    }

    try {
      // Mark as viewed first to prevent race conditions
      sessionStorage.setItem(sessionKey, 'true');
      await api.patch(`recent-activities/${id}/increment-view`);
    } catch (error) {
      console.error('Failed to increment view count:', error);
      // Remove the session marker if the API call failed
      sessionStorage.removeItem(sessionKey);
    }
  };

  // Fetch related activities
  const fetchRelatedActivities = async (categoryId, excludeId) => {
    try {
      const { data } = await api.get(`recent-activities/get?category=${categoryId}&limit=3&status=published`);
      if (data.success) {
        setRelatedActivities(data.data.activities.filter(act => act._id !== excludeId));
      }
    } catch (error) {
      console.error('Failed to fetch related activities:', error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchActivity();
      // Increment view count once per session
      incrementViewCount();
    }
  }, [id]);

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('bn-BD', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  // Get category color
  const getCategoryColor = (category) => {
    return category?.color || '#3B82F6';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4 py-16">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">কার্যক্রম পাওয়া যায়নি</h1>
            <p className="text-gray-600 mb-6">এই কার্যক্রমটি মুছে ফেলা হয়েছে বা উপলব্ধ নেই।</p>
            <Link to="/activities" className="btn btn-primary">
              <ArrowLeft size={20} />
              সকল কার্যক্রমে ফিরে যান
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Link 
            to="/activities"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors duration-200"
          >
            <ArrowLeft size={20} />
            সকল কার্যক্রমে ফিরে যান
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Featured Image */}
            <div className="relative rounded-xl overflow-hidden shadow-lg mb-6">
              <img
                src={`${baseUrl}${activity.imageUrl}`}
                alt={activity.title}
                className="w-full h-64 md:h-96 object-cover"
              />
              
              {/* Overlay badges */}
              <div className="absolute top-4 left-4 right-4 flex justify-between">
                <div className="flex gap-2">
                  {activity.featured && (
                    <div className="bg-yellow-500 text-white px-3 py-1 rounded-full flex items-center text-sm font-medium">
                      <Star size={16} className="mr-1 fill-current" />
                      বিশেষ কার্যক্রম
                    </div>
                  )}
                  
                  {activity.category && (
                    <span 
                      className="px-3 py-1 rounded-full text-white text-sm font-medium"
                      style={{ backgroundColor: getCategoryColor(activity.category) }}
                    >
                      {activity.category.name}
                    </span>
                  )}
                </div>
                
                <div className="bg-secondary text-white px-3 py-1 rounded-full flex items-center text-sm font-medium">
                  <Calendar size={14} className="mr-1" />
                  {formatDate(activity.activityDate)}
                </div>
              </div>
            </div>

            {/* Title and Content */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {activity.title}
              </h1>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                  {activity.shortDescription}
                </p>
                
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {activity.longDescription}
                </div>
              </div>
            </div>

            {/* Tags */}
            {activity.tags && activity.tags.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Tag size={20} className="mr-2 text-primary" />
                  ট্যাগসমূহ
                </h3>
                <div className="flex flex-wrap gap-2">
                  {activity.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-2 rounded-full text-sm bg-primary/10 text-primary border border-primary/20"
                    >
                      <Tag size={12} className="mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Activity Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">কার্যক্রমের তথ্য</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="text-primary" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">তারিখ</p>
                    <p className="font-medium">{formatDate(activity.activityDate)}</p>
                  </div>
                </div>

                {activity.location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="text-primary" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">স্থান</p>
                      <p className="font-medium">{activity.location}</p>
                    </div>
                  </div>
                )}

                {activity.participants > 0 && (
                  <div className="flex items-center gap-3">
                    <Users className="text-primary" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">অংশগ্রহণকারী</p>
                      <p className="font-medium">{activity.participants} জন</p>
                    </div>
                  </div>
                )}

                {activity.budget > 0 && (
                  <div className="flex items-center gap-3">
                    <DollarSign className="text-primary" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">বাজেট</p>
                      <p className="font-medium">৳{activity.budget.toLocaleString('bn-BD')}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Eye className="text-primary" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">দর্শক</p>
                    <p className="font-medium">{activity.views || 0} বার দেখা হয়েছে</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="text-primary" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">প্রকাশিত</p>
                    <p className="font-medium">{formatDate(activity.createdAt)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Share */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Share2 size={20} className="mr-2 text-primary" />
                শেয়ার করুন
              </h3>
              <p className="text-gray-600 text-sm">
                এই কার্যক্রমের তথ্য আপনার বন্ধুদের সাথে শেয়ার করুন
              </p>
              <div className="mt-4">
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: activity.title,
                        text: activity.shortDescription,
                        url: window.location.href,
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      alert('লিংক কপি হয়েছে!');
                    }
                  }}
                  className="btn btn-outline btn-primary w-full"
                >
                  <Share2 size={16} />
                  শেয়ার করুন
                </button>
              </div>
            </div>

            {/* Related Activities */}
            {relatedActivities.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">সংশ্লিষ্ট কার্যক্রম</h3>
                <div className="space-y-4">
                  {relatedActivities.slice(0, 3).map((relatedActivity) => (
                    <Link
                      key={relatedActivity._id}
                      to={`/activities/${relatedActivity._id}`}
                      className="block hover:bg-gray-50 rounded-lg p-3 transition-colors duration-200"
                    >
                      <div className="flex gap-3">
                        <img
                          src={`${baseUrl}${relatedActivity.imageUrl}`}
                          alt={relatedActivity.title}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 line-clamp-2 text-sm mb-1">
                            {relatedActivity.title}
                          </h4>
                          <p className="text-xs text-gray-500 flex items-center">
                            <Calendar size={12} className="mr-1" />
                            {formatDate(relatedActivity.activityDate)}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <Link to="/activities" className="text-primary hover:text-primary/80 text-sm font-medium">
                    সকল কার্যক্রম দেখুন →
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetail;